import { type ReactElement, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { Container, useSortItems } from '@optimization/ssi-common';
import Header from './Header';
import FiltersBar from './FiltersBar';
import Overview from './Overview';
import useCustomerCountries from './hooks/use-customer-countries';
import useCustomerNames from './hooks/use-customer-names';
import useChassisNumbers from './hooks/use-chassis-numbers';
import useBaseFilteredVehicles from './hooks/use-base-filtered-vehicles';
import useSegmentFilteredVehicles from './hooks/use-segment-filtered-vehicles';
import usePaginationValue from './hooks/use-pagination-value';
import useSelectedCountries from './hooks/use-selected-countries';
import useSelectedCustomers from './hooks/use-selected-customers';
import useSelectedChassisFilters from './hooks/use-selected-chassis-filters';
import useSelectedChassisNumber from './hooks/use-selected-chassisnumber';
import useResetAllQueryParameters from './hooks/use-reset-all-query-parameters';
import useSelectedVehicle from './hooks/use-selected-vehicle';
import useShowAlerts from './hooks/use-show-alerts';
import useSortColumn from './hooks/use-sort-column';
import useSortDirection from './hooks/use-sort-direction';
import useSortIndicator from './hooks/use-sort-indicator';
import useRowsPerPage from './hooks/use-rows-per-page';
import useOpenVehicleDetail from './hooks/use-open-vehicle-detail';
import useCloseVehicleDetail from './hooks/use-close-vehicle-detail';
import useSelectedIndex from './hooks/use-selected-index';
import useSelectedChartSegment from './hooks/use-selected-chart-segment';
import useSelectedBatteries from './hooks/use-selected-batteries';
import useSelectedConfigurations from './hooks/use-selected-configurations';
import useSelectedVehicleTypes from './hooks/use-selected-vehicle-types';
import useSelectedPropulsions from './hooks/use-selected-propulsions';
import useBatteries from './hooks/use-batteries';
import useConfigurations from './hooks/use-configurations';
import useOverviewChartVehicles from './hooks/use-overview-chart-vehicles';
import VehicleDetail from './VehicleDetail';
import styles from './style.module.scss';
import VehicleList from './VehicleList';
import useUpdateQueryParameterValues from './hooks/use-update-query-parameter-values';
import { sortFunctions } from './utils/sort';
import GlobalLoading from '@/common/components/GlobalLoading';
import ErrorBoundary from '@/common/components/ErrorBoundary';
import { getVehiclesQueryOptions } from '@/features/BatteryMonitoring/services/battery-monitoring-api/utils/get-vehicles-query-options';
import { useSiteNotices } from '@/providers/site-notice-provider/utils/use-site-notices';
import {
  cycledEnergyPerKilometerConfig,
  cycledEnergyPerKilometerSegments,
  stateOfHealthConfig,
  stateOfHealthSegments,
} from '@/app/config/chart-segments';

const BatteryMonitoring = (): ReactElement => {
  const paginationValue = usePaginationValue();
  const rowsPerPage = useRowsPerPage();
  const sortColumn = useSortColumn(null);
  const sortDirection = useSortDirection();
  const sortIndicator = useSortIndicator({ sortColumn, sortDirection });

  const selectedCountries = useSelectedCountries();
  const selectedCustomers = useSelectedCustomers();
  const selectedChassisFilters = useSelectedChassisFilters();
  const selectedChassisNumber = useSelectedChassisNumber();
  const selectedBatteries = useSelectedBatteries();
  const selectedConfigurations = useSelectedConfigurations();
  const selectedVehicleTypes = useSelectedVehicleTypes();
  const selectedPropulsions = useSelectedPropulsions();
  const selectedStateOfHealthIndex = useSelectedIndex({ indexKey: stateOfHealthConfig.indexKey });
  const selectedCycledEnergyIndex = useSelectedIndex({ indexKey: cycledEnergyPerKilometerConfig.indexKey });
  const showAlerts = useShowAlerts();

  const selectedStateOfHealthSegment = useSelectedChartSegment({
    segments: stateOfHealthSegments,
    selectedIndex: selectedStateOfHealthIndex,
  });

  const selectedCycledEnergySegment = useSelectedChartSegment({
    segments: cycledEnergyPerKilometerSegments,
    selectedIndex: selectedCycledEnergyIndex,
  });

  const vehiclesQuery = useQuery({ ...getVehiclesQueryOptions() });
  const { showTechnicalIssuesNotice, showNoAccessNotice } = useSiteNotices();
  const vehicleQueryErrorStatus = isAxiosError(vehiclesQuery.error) ? vehiclesQuery.error.response?.status : undefined;
  const isNoAccessError = vehicleQueryErrorStatus === 401 || vehicleQueryErrorStatus === 403;

  useEffect(() => {
    if (vehicleQueryErrorStatus === 500) {
      showTechnicalIssuesNotice();
    }

    if (isNoAccessError) {
      showNoAccessNotice();
    }
  }, [isNoAccessError, showNoAccessNotice, showTechnicalIssuesNotice, vehicleQueryErrorStatus]);

  const countries = useCustomerCountries(vehiclesQuery.data?.records);
  const customers = useCustomerNames({ vehicles: vehiclesQuery.data?.records, selectedCountries });
  const chassisNumbers = useChassisNumbers({
    vehicles: vehiclesQuery.data?.records,
    selectedCountries,
    selectedCustomers,
  });
  const configurations = useConfigurations({ vehicles: vehiclesQuery.data?.records });
  const batteries = useBatteries({ vehicles: vehiclesQuery.data?.records });

  const selectedVehicle = useSelectedVehicle({
    vehicles: vehiclesQuery.data?.records,
    selectedChassisNumber,
  });

  const baseFilteredVehicles = useBaseFilteredVehicles({
    vehicles: vehiclesQuery.data?.records,
    selectedCountries,
    selectedCustomers,
    selectedChassisFilters,
    selectedBatteries,
    selectedConfigurations,
    selectedVehicleTypes,
    selectedPropulsions,
    showAlerts,
  });

  const segmentFilteredVehicles = useSegmentFilteredVehicles({
    baseFilteredVehicles,
    selectedStateOfHealthSegment,
    selectedCycledEnergySegment,
  });
  const { stateOfHealthChartVehicles, cycledPerKilometerChartVehicles } = useOverviewChartVehicles({
    baseFilteredVehicles,
    selectedStateOfHealthSegment,
    selectedCycledEnergySegment,
  });

  const sortedVehicles = useSortItems({
    items: segmentFilteredVehicles,
    sortType: sortIndicator?.columnKey,
    sortDirection: sortIndicator?.sortingDirection,
    sortFunctions,
  });

  const updateQueryParameterValues = useUpdateQueryParameterValues({ vehicles: vehiclesQuery.data?.records });
  const resetAllQueryParameters = useResetAllQueryParameters();

  const openVehicleDetail = useOpenVehicleDetail({ updateQueryParameterValues });
  const closeVehicleDetail = useCloseVehicleDetail({ updateQueryParameterValues });

  const isLoading = vehiclesQuery.isLoading;
  const isError = vehiclesQuery.isError;

  return (
    <Container className={styles.container}>
      <GlobalLoading isLoading={isLoading} isError={isError && !isNoAccessError} />
      {selectedVehicle && (
        <ErrorBoundary>
          <VehicleDetail selectedVehicle={selectedVehicle} closeVehicleDetail={closeVehicleDetail} />
        </ErrorBoundary>
      )}
      {sortedVehicles && stateOfHealthChartVehicles && cycledPerKilometerChartVehicles && (
        <>
          <ErrorBoundary>
            <Header />
          </ErrorBoundary>
          <ErrorBoundary>
            <FiltersBar
              countries={countries}
              customers={customers}
              chassisNumbers={chassisNumbers}
              selectedCountries={selectedCountries}
              selectedCustomers={selectedCustomers}
              selectedChassisFilters={selectedChassisFilters}
              disabled={isLoading}
              updateQueryParameterValues={updateQueryParameterValues}
              resetAllQueryParameters={resetAllQueryParameters}
            />
          </ErrorBoundary>
          <ErrorBoundary>
            <Overview
              stateOfHealthChartVehicles={stateOfHealthChartVehicles}
              cycledPerKilometerChartVehicles={cycledPerKilometerChartVehicles}
              sortedVehicles={sortedVehicles}
              selectedStateOfHealthSegment={selectedStateOfHealthSegment}
              selectedCycledEnergySegment={selectedCycledEnergySegment}
              updateQueryParameterValues={updateQueryParameterValues}
            />
          </ErrorBoundary>
          <ErrorBoundary>
            <VehicleList
              paginationValue={paginationValue}
              rowsPerPage={rowsPerPage}
              sortedVehiclesCount={sortedVehicles.length}
              sortedVehicles={sortedVehicles}
              sortIndicator={sortIndicator}
              selectedCustomers={selectedCustomers}
              selectedChassisFilters={selectedChassisFilters}
              selectedStateOfHealthSegment={selectedStateOfHealthSegment}
              selectedCycledEnergySegment={selectedCycledEnergySegment}
              selectedBatteries={selectedBatteries}
              selectedConfigurations={selectedConfigurations}
              selectedVehicleTypes={selectedVehicleTypes}
              selectedPropulsions={selectedPropulsions}
              batteries={batteries}
              configurations={configurations}
              showAlerts={showAlerts}
              openVehicleDetail={openVehicleDetail}
              updateQueryParameterValues={updateQueryParameterValues}
            />
          </ErrorBoundary>
        </>
      )}
    </Container>
  );
};

export default BatteryMonitoring;
